import {
  ArrowRight,
  BellRing,
  BookmarkCheck,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "../../../api/client";
import { confirmEmailCode, requestPasswordReset, resetPassword, resendRegistrationCode } from "../api";
import { useAccount } from "../useAccount";

type AuthMode = "login" | "register" | "recover" | "verify" | "reset";

const RESEND_CODE_COOLDOWN_SECONDS = 60;

function modeFromPathname(pathname: string): AuthMode {
  if (pathname === "/auth/register") {
    return "register";
  }
  if (pathname === "/auth/recover") {
    return "recover";
  }
  if (pathname === "/auth/verify") {
    return "verify";
  }
  if (pathname === "/auth/reset") {
    return "reset";
  }
  return "login";
}

function navigate(pathname: string): void {
  window.location.assign(pathname);
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message;
  }
  return "Не удалось выполнить запрос. Попробуйте ещё раз.";
}

function YandexIdMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1.6 15.6H10.9V13l-3.4-6.6h2.8l2 4.2 2-4.2h2.8l-3.5 6.7v4.5Z" />
    </svg>
  );
}

export function AuthPage() {
  const { login, register } = useAccount();
  const [mode, setMode] = useState<AuthMode>(() =>
    modeFromPathname(window.location.pathname),
  );
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [resetToken] = useState(() => new URLSearchParams(window.location.search).get("token") ?? "");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acknowledgedPrivacy, setAcknowledgedPrivacy] = useState(false);
  const [confirmedAdult, setConfirmedAdult] = useState(false);
  const [subscribedToNewsletter, setSubscribedToNewsletter] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [triedToSubmit, setTriedToSubmit] = useState(false);
  const [shakeInvalid, setShakeInvalid] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordConfirmationVisible, setPasswordConfirmationVisible] = useState(false);
  const [vkSdk, setVkSdk] = useState<any>(null);
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  useEffect(() => {
    let disposed = false;
    const configure = () => {
      const vk = (window as Window & { VKIDSDK?: any }).VKIDSDK;
      if (!vk || disposed) return;
      vk.Config.init({ app: 54776967, redirectUrl: "https://neluminar.com/api/v1/auth/vk/callback", responseMode: vk.ConfigResponseMode.Callback, scope: "email" });
      setVkSdk(vk);
    };
    const existing = document.querySelector<HTMLScriptElement>('script[data-vk-id-sdk="true"]');
    if (existing) {
      if ((window as Window & { VKIDSDK?: any }).VKIDSDK) configure();
      else existing.addEventListener("load", configure, { once: true });
    } else {
      const script = document.createElement("script");
      script.dataset.vkIdSdk = "true";
      script.src = "https://unpkg.com/@vkid/sdk@<3.0.0/dist-sdk/umd/index.js";
      script.async = true;
      script.onload = configure;
      script.onerror = () => setError("Не удалось загрузить VK ID.");
      document.head.appendChild(script);
    }
    return () => { disposed = true; };
  }, []);

  useEffect(() => {
    if (mode !== "verify" || !email) return;
    const savedDeadline = Number(sessionStorage.getItem(`neluminar-resend-code:${email}`));
    if (savedDeadline > Date.now()) {
      setResendAvailableAt(savedDeadline);
      setCurrentTime(Date.now());
    }
  }, [email, mode]);

  useEffect(() => {
    if (mode !== "verify" || resendAvailableAt <= Date.now()) return;
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [mode, resendAvailableAt]);

  const loginWithVk = async () => {
    if (mode === "register" && !(acceptedTerms && acknowledgedPrivacy && confirmedAdult)) {
      setTriedToSubmit(true);
      setError("Подтвердите условия использования, политику и возраст 18+.");
      return;
    }
    if (!vkSdk) {
      setError("VK ID ещё загружается. Попробуйте через секунду.");
      return;
    }
    setError("");
    try {
      const payload = await vkSdk.Auth.login({ scheme: vkSdk.Scheme.DARK });
      const tokenResponse = await vkSdk.Auth.exchangeCode(payload.code, payload.device_id);
      await fetch("/api/v1/auth/csrf", { credentials: "include" });
      const csrfToken = document.cookie.match(/neluminar_csrf=([^;]+)/)?.[1] || "";
      const response = await fetch("/api/v1/auth/vk/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({
          access_token: tokenResponse.access_token,
          terms_accepted: mode === "register" && acceptedTerms,
          privacy_acknowledged: mode === "register" && acknowledgedPrivacy,
          age_confirmed: mode === "register" && confirmedAdult,
          marketing_consent: mode === "register" && subscribedToNewsletter,
        }),
        credentials: "include",
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.message || "VK ID отклонил запрос на вход.");
      navigate("/table");
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "Не удалось войти через VK ID.");
    }
  };
  const emailIsReady = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const passwordSetupMode = mode === "register" || mode === "reset";
  const showPassword = mode === "login" || mode === "reset" || (mode === "register" && emailIsReady);
  const showPasswordConfirmation = passwordSetupMode && password.length > 0;
  const passwordRequirements = [
    { label: "Не менее 10 символов", passed: password.length >= 10 },
    { label: "Хотя бы одна заглавная буква", passed: /\p{Lu}/u.test(password) },
    { label: "Цифра и специальный символ", passed: /\p{Nd}/u.test(password) && /[^\p{L}\p{N}\s]/u.test(password) },
  ];
  const registrationPasswordIsValid = passwordRequirements.every(({ passed }) => passed);
  const passwordsDoNotMatch = showPasswordConfirmation && password !== passwordConfirmation;
  const nameInvalid = mode === "register" && triedToSubmit && displayName.trim().length < 2;
  const emailInvalid = triedToSubmit && !emailIsReady;
  const passwordInvalid = (mode === "login" || passwordSetupMode) && showPassword && triedToSubmit && (passwordSetupMode ? !registrationPasswordIsValid : password.length === 0);
  const confirmationInvalid = showPasswordConfirmation && triedToSubmit && passwordsDoNotMatch;
  const passwordMismatchInvalid = passwordSetupMode && triedToSubmit && passwordsDoNotMatch;
  const termsInvalid = mode === "register" && triedToSubmit && !acceptedTerms;
  const privacyInvalid = mode === "register" && triedToSubmit && !acknowledgedPrivacy;
  const ageInvalid = mode === "register" && triedToSubmit && !confirmedAdult;
  const codeInvalid = mode === "verify" && triedToSubmit && !/^\d{6}$/.test(verificationCode);
  const resendSeconds = Math.max(0, Math.ceil((resendAvailableAt - currentTime) / 1000));
  const resendDisabled = isResendingCode || resendSeconds > 0;

  const startResendCooldown = (seconds: number) => {
    const deadline = Date.now() + seconds * 1000;
    sessionStorage.setItem(`neluminar-resend-code:${email}`, String(deadline));
    setResendAvailableAt(deadline);
    setCurrentTime(Date.now());
  };

  const changeMode = (nextMode: AuthMode) => {
    setMode(nextMode);
    setMessage("");
    setError("");
    setPassword("");
    setPasswordConfirmation("");
    setPasswordVisible(false);
    setPasswordConfirmationVisible(false);
    setVerificationCode("");
    setTriedToSubmit(false);
    const pathname =
      nextMode === "register"
        ? "/auth/register"
        : nextMode === "recover"
          ? "/auth/recover"
        : nextMode === "verify"
          ? "/auth/verify"
          : nextMode === "reset"
            ? "/auth/reset"
          : "/auth";
    window.history.pushState(null, "", pathname);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setTriedToSubmit(true);
    const invalidRegistration =
      mode === "register" &&
      (!emailIsReady || displayName.trim().length < 2 || !registrationPasswordIsValid || password !== passwordConfirmation || !acceptedTerms || !acknowledgedPrivacy || !confirmedAdult);
    const invalidLogin = mode === "login" && (!emailIsReady || password.length === 0);
    const invalidRecovery = mode === "recover" && !emailIsReady;
    const invalidVerification = mode === "verify" && (!emailIsReady || !/^\d{6}$/.test(verificationCode));
    const invalidReset = mode === "reset" && (!resetToken || !registrationPasswordIsValid || password !== passwordConfirmation);
    if (invalidRegistration || invalidLogin || invalidRecovery || invalidVerification || invalidReset) {
      setShakeInvalid(false);
      window.requestAnimationFrame(() => setShakeInvalid(true));
      setError(passwordsDoNotMatch ? "Пароли не совпадают. Проверьте оба поля." : "Проверьте выделенные поля.");
      return;
    }
    setIsSubmitting(true);
    try {
      if (mode === "register") {
        const response = await register({
          email,
          display_name: displayName,
          password,
          terms_accepted: acceptedTerms,
          privacy_acknowledged: acknowledgedPrivacy,
          age_confirmed: confirmedAdult,
          marketing_consent: subscribedToNewsletter,
        });
        setMode("login");
        window.history.pushState(null, "", "/auth/verify");
        setMode("verify");
        startResendCooldown(RESEND_CODE_COOLDOWN_SECONDS);
        setMessage(response.message);
      } else if (mode === "recover") {
        const response = await requestPasswordReset(email);
        setMessage(response.message);
      } else if (mode === "verify") {
        await confirmEmailCode(email, verificationCode);
        navigate("/table");
      } else if (mode === "reset") {
        const response = await resetPassword(resetToken, password);
        changeMode("login");
        setMessage(response.message);
      } else {
        await login({ email, password });
        navigate("/table");
      }
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const title =
    mode === "register"
      ? "Создание аккаунта"
      : mode === "recover"
        ? "Восстановите доступ"
        : mode === "verify"
          ? "Подтвердите почту"
          : mode === "reset"
            ? "Новый пароль"
          : "С возвращением";
  const subtitle =
    mode === "register"
      ? "Заполните данные для регистрации."
      : mode === "recover"
        ? "Отправим одноразовую ссылку для создания нового пароля."
        : mode === "verify"
          ? "Введите шестизначный код, который мы отправили на вашу почту."
          : mode === "reset"
            ? "Придумайте новый пароль для своего аккаунта."
          : "Войдите, чтобы продолжить работу со своим портфелем.";

  return (
    <main className="auth-page">
      <a className="auth-page-brand" href="/table" aria-label="NeLuminar, к таблице">
        <img src="/neluminar-logo.svg" alt="" />
        <span>NeLuminar</span>
      </a>

      <section className="auth-page-showcase" aria-label="Возможности NeLuminar">
        <div className="auth-showcase-copy">
          <h1>После регистрации всё важное будет под рукой.</h1>
          <p>
            Аккаунт сохраняет ваш рабочий контекст и помогает не терять
            интересные возможности на рынке.
          </p>
        </div>
        <div className="auth-benefit-list">
          <article className="auth-benefit-card">
            <span className="auth-benefit-icon"><BookmarkCheck aria-hidden="true" /></span>
            <div><strong>Сохранённые сделки</strong><p>Собирайте интересные связки рынков в одном месте.</p></div>
          </article>
          <article className="auth-benefit-card">
            <span className="auth-benefit-icon"><SlidersHorizontal aria-hidden="true" /></span>
            <div><strong>Личные фильтры</strong><p>Настройте таблицу под свой стиль поиска и не повторяйте работу.</p></div>
          </article>
          <article className="auth-benefit-card">
            <span className="auth-benefit-icon"><BellRing aria-hidden="true" /></span>
            <div><strong>Уведомления о ценах</strong><p>Получайте сигнал, когда цена или прибыльность достигает цели.</p></div>
          </article>
        </div>
        <div className="auth-showcase-points">
          <span><ShieldCheck aria-hidden="true" /> Защищённый вход</span>
          <span><KeyRound aria-hidden="true" /> Контроль сессий</span>
        </div>
      </section>

      <section className="auth-page-form-area">
        <div className="auth-page-form-wrap">
          <div className="auth-page-heading">
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>

          {mode === "login" || mode === "register" ? (
            <div className="auth-page-tabs" role="tablist" aria-label="Авторизация">
              <button
                type="button"
                className={mode === "login" ? "is-active" : ""}
                onClick={() => changeMode("login")}
              >
                Вход
              </button>
              <button
                type="button"
                className={mode === "register" ? "is-active" : ""}
                onClick={() => changeMode("register")}
              >
                Регистрация
              </button>
            </div>
          ) : null}

          <form className="auth-page-form" onSubmit={submit} noValidate>
            {mode === "register" ? (
              <label>
                <span>Имя</span>
                <span className={`auth-input-shell${nameInvalid ? " is-invalid" : ""}${nameInvalid && shakeInvalid ? " is-shaking" : ""}`}>
                  <UserRound aria-hidden="true" />
                  <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" placeholder="username" minLength={2} maxLength={80} aria-invalid={nameInvalid} required />
                </span>
              </label>
            ) : null}
            {mode !== "reset" ? (
              <label>
                <span>Электронная почта</span>
                <span className={`auth-input-shell${emailInvalid ? " is-invalid" : ""}${emailInvalid && shakeInvalid ? " is-shaking" : ""}`}>
                  <Mail aria-hidden="true" />
                  <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="you@example.com" aria-invalid={emailInvalid} required />
                </span>
              </label>
            ) : null}
            {showPassword ? (
              <label className={passwordSetupMode ? "auth-progressive-field" : undefined}>
                <span>Пароль</span>
                <span className={`auth-input-shell auth-password-shell${passwordInvalid || passwordMismatchInvalid ? " is-invalid" : ""}${(passwordInvalid || passwordMismatchInvalid) && shakeInvalid ? " is-shaking" : ""}`}>
                  <KeyRound aria-hidden="true" />
                  <input type={passwordVisible ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={passwordSetupMode ? "new-password" : "current-password"} minLength={passwordSetupMode ? 10 : 1} aria-invalid={passwordInvalid || passwordMismatchInvalid} required />
                  <button className="auth-password-visibility" type="button" onClick={() => setPasswordVisible((visible) => !visible)} aria-label={passwordVisible ? "Скрыть пароль" : "Показать пароль"} aria-pressed={passwordVisible}>
                    {passwordVisible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                  </button>
                </span>
                {passwordSetupMode ? (
                  <ul className="auth-password-requirements" aria-label="Требования к паролю">
                    {passwordRequirements.map(({ label, passed }) => (
                      <li className={passed ? "is-passed" : ""} key={label}>
                        <span className="auth-password-status" aria-hidden="true" />
                        {label}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </label>
            ) : null}
            {showPasswordConfirmation ? (
              <label className="auth-progressive-field">
                <span>Подтвердите пароль</span>
                <span className={`auth-input-shell auth-password-shell${confirmationInvalid ? " is-invalid" : ""}${confirmationInvalid && shakeInvalid ? " is-shaking" : ""}`}>
                  <KeyRound aria-hidden="true" />
                  <input
                    type={passwordConfirmationVisible ? "text" : "password"}
                    value={passwordConfirmation}
                    onChange={(event) => setPasswordConfirmation(event.target.value)}
                    autoComplete="new-password"
                    minLength={10}
                    aria-invalid={confirmationInvalid}
                    required
                  />
                  <button className="auth-password-visibility" type="button" onClick={() => setPasswordConfirmationVisible((visible) => !visible)} aria-label={passwordConfirmationVisible ? "Скрыть пароль" : "Показать пароль"} aria-pressed={passwordConfirmationVisible}>
                    {passwordConfirmationVisible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                  </button>
                </span>
                {confirmationInvalid ? <span className="auth-field-error">Пароли не совпадают. Проверьте оба поля.</span> : null}
              </label>
            ) : null}
            {mode === "verify" ? (
              <label className="auth-progressive-field">
                <span>Код подтверждения</span>
                <span className={`auth-input-shell auth-code-input${codeInvalid && shakeInvalid ? " is-shaking" : ""}`}>
                  <KeyRound aria-hidden="true" />
                  <input
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    value={verificationCode}
                    onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                    autoComplete="one-time-code"
                    placeholder="000000"
                    aria-invalid={codeInvalid}
                    required
                  />
                </span>
              </label>
            ) : null}
            {mode === "register" ? (
              <div className="auth-consent-list">
                <label className={`auth-checkbox-row${termsInvalid ? " is-invalid" : ""}${termsInvalid && shakeInvalid ? " is-shaking" : ""}`}>
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(event) => setAcceptedTerms(event.target.checked)}
                    required
                  />
                  <span>
                    Я принимаю <a href="/legal/terms" target="_blank" rel="noreferrer">условия использования</a>.
                  </span>
                </label>
                <label className={`auth-checkbox-row${privacyInvalid ? " is-invalid" : ""}${privacyInvalid && shakeInvalid ? " is-shaking" : ""}`}>
                  <input type="checkbox" checked={acknowledgedPrivacy} onChange={(event) => setAcknowledgedPrivacy(event.target.checked)} required />
                  <span>Я ознакомился с <a href="/legal/privacy" target="_blank" rel="noreferrer">политикой обработки персональных данных</a>.</span>
                </label>
                <label className={`auth-checkbox-row${ageInvalid ? " is-invalid" : ""}${ageInvalid && shakeInvalid ? " is-shaking" : ""}`}>
                  <input type="checkbox" checked={confirmedAdult} onChange={(event) => setConfirmedAdult(event.target.checked)} required />
                  <span>Подтверждаю, что мне исполнилось 18 лет.</span>
                </label>
                <label className="auth-checkbox-row">
                  <input
                    type="checkbox"
                    checked={subscribedToNewsletter}
                    onChange={(event) => setSubscribedToNewsletter(event.target.checked)}
                  />
                  <span>Хочу получать новости сервиса и редкие полезные письма.</span>
                </label>
              </div>
            ) : null}
            {error ? <div className="auth-page-message is-error">{error}</div> : null}
            {message ? <div className="auth-page-message">{message}</div> : null}
            <button className="auth-page-submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Подождите…" : mode === "register" ? "Создать аккаунт" : mode === "recover" ? "Отправить ссылку" : mode === "verify" ? "Подтвердить почту" : mode === "reset" ? "Сохранить пароль" : "Войти"}
              {!isSubmitting ? <ArrowRight aria-hidden="true" /> : null}
            </button>
          </form>

          {mode === "login" ? (
            <button className="auth-page-recover" type="button" onClick={() => changeMode("recover")}>
              Не помню пароль
            </button>
          ) : mode === "recover" ? (
            <button className="auth-page-recover" type="button" onClick={() => changeMode("login")}>
              Вернуться ко входу
            </button>
          ) : mode === "verify" ? (
            <div className="auth-page-verify-actions">
              <button
                className="auth-page-recover"
                type="button"
                disabled={resendDisabled}
                onClick={() => {
                  if (resendDisabled) return;
                  setError("");
                  setMessage("");
                  setIsResendingCode(true);
                  void resendRegistrationCode(email)
                    .then((response) => {
                      startResendCooldown(RESEND_CODE_COOLDOWN_SECONDS);
                      setMessage(response.message);
                    })
                    .catch((requestError) => {
                      if (requestError instanceof ApiError && requestError.status === 429 && requestError.retryAfter) {
                        startResendCooldown(requestError.retryAfter);
                        return;
                      }
                      setError(errorMessage(requestError));
                    })
                    .finally(() => setIsResendingCode(false));
                }}
              >
                {resendSeconds > 0 ? `Отправить код повторно через ${resendSeconds} сек.` : "Отправить код повторно"}
              </button>
              <button className="auth-page-recover" type="button" onClick={() => changeMode("login")}>
                Вернуться ко входу
              </button>
            </div>
          ) : mode === "reset" ? (
            <button className="auth-page-recover" type="button" onClick={() => changeMode("login")}>
              Вернуться ко входу
            </button>
          ) : null}

          {mode === "login" || mode === "register" ? (
            <>
              <div className="auth-page-divider"><span>Войти с помощью</span></div>
              <div className="auth-provider-buttons">
                <button className="auth-provider-link auth-provider-yandex" type="button" onClick={() => {
                  if (mode === "register" && !(acceptedTerms && acknowledgedPrivacy && confirmedAdult)) {
                    setTriedToSubmit(true); setError("Подтвердите условия использования, политику и возраст 18+."); return;
                  }
                  const params = mode === "register" ? "?registration=1&terms_accepted=1&privacy_acknowledged=1&age_confirmed=1" + (subscribedToNewsletter ? "&marketing_consent=1" : "") : "";
                  window.location.assign(`/api/v1/auth/yandex/start${params}`);
                }} title="Продолжить с Яндекс ID" aria-label="Войти через Яндекс ID"><YandexIdMark /></button>
                <button className="auth-provider-vk" type="button" onClick={() => void loginWithVk()} aria-label="Войти через VK ID">
                  <img src="/vk-id-mark.svg" alt="" />
                </button>
              </div>
            </>
          ) : null}
          <p className="auth-page-legal"><a href="/legal/terms">Условия использования</a> · <a href="/legal/privacy">Политика обработки персональных данных</a> · <a href="/legal/cookies">Cookies</a></p>
        </div>
      </section>
    </main>
  );
}
